import React, { useState } from 'react';
import { MessageSquare, Send, ArrowRight, UserCheck, Edit, Check } from 'lucide-react';
import { Button } from './ui/button';
import { evaluateSubmission, type SubmissionScore } from '../lib/scoring';
import { useSubmissions } from '@/lib/hooks/useSubmissions';

interface FilmmakerQuestionnaireProps {
  studioId?: string;
}

interface Question {
  id: string;
  text: string;
  aiPrompt: string;
  type?: 'text' | 'longtext' | 'multiselect' | 'link';
  options?: string[];
  required?: boolean;
}

const questions: Question[] = [
  {
    id: 'name',
    text: 'Your name',
    aiPrompt: 'Please provide your full name as you\'d like it to appear on official documents.',
    type: 'text',
    required: true
  },
  {
    id: 'role',
    text: 'Your role',
    aiPrompt: 'Select your primary role in this project.',
    type: 'multiselect',
    options: ['EP', 'Director', 'Producer', 'Other'],
    required: true
  },
  {
    id: 'organization',
    text: 'Your organization',
    aiPrompt: 'The name of your production company or organization, if applicable.',
    type: 'text',
    required: true
  },
  {
    id: 'email',
    text: 'Primary contact email',
    aiPrompt: 'This will be our main method of communication.',
    type: 'text',
    required: true
  },
  {
    id: 'projectTitle',
    text: 'What\'s the title of your project?',
    aiPrompt: 'Current working title - this can be updated later.',
    type: 'text',
    required: true
  },
  {
    id: 'format',
    text: 'Which formats are you working with?',
    aiPrompt: 'Select all applicable formats for your project.',
    type: 'multiselect',
    options: [
      'Documentary Feature',
      'Documentary Series',
      'Documentary Short',
      'Narrative Feature',
      'Narrative Series',
      'Narrative Short',
      'Social Media Shorts',
      'Podcast'
    ],
    required: true
  },
  {
    id: 'essence',
    text: 'What\'s the essence of your project?',
    aiPrompt: 'In a few sentences, capture the core idea or heart of your project.',
    type: 'longtext',
    required: true
  },
  {
    id: 'synopsis',
    text: 'Give us the synopsis of the story you\'re aiming to tell.',
    aiPrompt: 'Provide a clear and compelling overview of your story.',
    type: 'longtext',
    required: true
  },
  {
    id: 'currentPhase',
    text: 'What phase is your project currently in?',
    aiPrompt: 'Select the current stage of your project.',
    type: 'multiselect',
    options: [
      'Idea',
      'Development',
      'Pre-production',
      'Production',
      'Post-production'
    ],
    required: true
  },
  {
    id: 'mainFigures',
    text: 'Who are the main figures in your narrative?',
    aiPrompt: 'Describe the key characters or subjects in your story.',
    type: 'longtext',
    required: true
  },
  {
    id: 'storyArc',
    text: 'Describe the arc of your story or the structure of your concept.',
    aiPrompt: 'How does your story unfold? What\'s the narrative journey?',
    type: 'longtext',
    required: true
  },
  {
    id: 'whyThisStory',
    text: 'Why do you have this project? Share about the themes or issues at its heart and why they matter to you.',
    aiPrompt: 'Help us understand your personal connection to this story.',
    type: 'longtext',
    required: true
  },
  {
    id: 'impact',
    text: 'What change or impact do you hope to inspire?',
    aiPrompt: 'What effect do you want your project to have on its audience and the world?',
    type: 'longtext',
    required: true
  },
  {
    id: 'audience',
    text: 'Who do you envision as the audience for your film?',
    aiPrompt: 'Describe your target audience and why this story will resonate with them.',
    type: 'longtext',
    required: true
  },
  {
    id: 'artisticApproach',
    text: 'How do you plan to bring this project to life? Tell us about your artistic approach and any creative traditions or innovations you\'re drawing from.',
    aiPrompt: 'Describe your creative vision and how you plan to execute it.',
    type: 'longtext',
    required: true
  },
  {
    id: 'visualMaterials',
    text: 'Please share any visual materials that would help us understand your approach. For example, pitch decks, teasers, scene selects, mood board, or other edited footage (up to 15 minutes).',
    aiPrompt: 'Provide links to any visual materials that demonstrate your vision.',
    type: 'link',
    required: true
  },
  {
    id: 'productionCompany',
    text: 'Tell us about your production company or your journey as a filmmaker.',
    aiPrompt: 'Share your background and experience in filmmaking.',
    type: 'longtext',
    required: true
  },
  {
    id: 'team',
    text: 'Who\'s helping you tell this story? Introduce us to your main creative partners and their roles.',
    aiPrompt: 'Tell us about your key collaborators and what they bring to the project.',
    type: 'longtext',
    required: true
  },
  {
    id: 'proudProject',
    text: 'Tell us about the project you\'re most proud of. What was it about? Why does it stand out to you? What did you learn from making it?',
    aiPrompt: 'Share a significant project from your past and what it taught you.',
    type: 'longtext',
    required: true
  },
  {
    id: 'workSample',
    text: 'Can you share a sample of that work? A trailer, teaser, or any clip that gives us a sense of your storytelling style. (Please provide a link.)',
    aiPrompt: 'Share a link to a sample of your previous work.',
    type: 'link',
    required: true
  },
  {
    id: 'impact',
    text: 'Please tell us about any measurable impact - audience reactions, or lessons you took from the experience. Did it spark a conversation, change a policy, or reach a specific audience in a meaningful way?',
    aiPrompt: 'Describe the concrete impact of your previous work.',
    type: 'longtext',
    required: true
  },
  {
    id: 'budget',
    text: 'What is your estimated budget range?',
    aiPrompt: 'Select the budget range that best fits your project.',
    type: 'multiselect',
    options: [
      'Under $100k',
      '$100k-$500k',
      '$500k-$1M',
      '$1M-$5M',
      'Over $5M'
    ],
    required: true
  },
  {
    id: 'timeline',
    text: 'What is your anticipated timeline for completion?',
    aiPrompt: 'When do you expect to complete the project?',
    type: 'text',
    required: true
  },
  {
    id: 'fundingStatus',
    text: 'What is your current funding status?',
    aiPrompt: 'Tell us about any secured funding or pending applications.',
    type: 'longtext',
    required: true
  },
  {
    id: 'distribution',
    text: 'What are your distribution goals?',
    aiPrompt: 'How do you envision your project reaching its audience?',
    type: 'longtext',
    required: true
  },
  {
    id: 'support',
    text: 'What type of support are you seeking?',
    aiPrompt: 'What specific resources or assistance would be most helpful?',
    type: 'multiselect',
    options: [
      'Funding',
      'Creative Development',
      'Production Support',
      'Post-production Support',
      'Impact Strategy',
      'Distribution Strategy',
      'Marketing Support'
    ],
    required: true
  },
  {
    id: 'challenges',
    text: 'What are the main challenges you anticipate?',
    aiPrompt: 'What obstacles do you foresee and how do you plan to address them?',
    type: 'longtext',
    required: true
  },
  {
    id: 'uniqueness',
    text: 'What makes your project unique?',
    aiPrompt: 'What sets your project apart from similar works?',
    type: 'longtext',
    required: true
  },
  {
    id: 'additionalInfo',
    text: 'Is there anything else you\'d like to share?',
    aiPrompt: 'Share any additional information that would help us understand your project better.',
    type: 'longtext',
    required: false
  }
];

export function FilmmakerQuestionnaire({ studioId }: FilmmakerQuestionnaireProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [submissionScore, setSubmissionScore] = useState<SubmissionScore | null>(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const { createSubmission } = useSubmissions(studioId);

  const currentQuestion = questions[currentQuestionIndex];

  const handleNext = () => {
    if (currentQuestion.type === 'multiselect' ? selectedOptions.length > 0 : currentAnswer.trim()) {
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: currentQuestion.type === 'multiselect' ? selectedOptions : currentAnswer
      }));
      setCurrentAnswer('');
      setSelectedOptions([]);
      setShowAiPrompt(false);
      
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (!studioId) return;

    try {
      const submissionData = {
        studio_id: studioId,
        name: answers.name as string,
        email: answers.email as string,
        project_title: answers.projectTitle as string,
        project_format: answers.format as string[],
        synopsis: answers.synopsis as string,
        current_phase: answers.currentPhase as string[],
        budget_range: answers.budget as string[],
        custom_answers: answers
      };

      await createSubmission(submissionData);

      const studioPreferences = {
        genres: ['Documentary', 'Drama'],
        budgetRanges: ['$500k-$1M', '$1M-$5M'],
        targetAudience: 'Socially conscious viewers aged 25-45 who are interested in thought-provoking content and cultural documentaries',
        distribution: ['Streaming Platforms', 'Film Festivals'],
        mission: 'Creating impactful documentaries and dramas that inspire social change and cultural understanding'
      };

      const score = evaluateSubmission(answers, studioPreferences);
      setSubmissionScore(score);
      setShowScoreModal(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      // Handle error appropriately
    }
  };

  const toggleOption = (option: string) => {
    setSelectedOptions(prev => 
      prev.includes(option)
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="bg-[#212323] rounded-lg border border-gray-700 p-8 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            Question {currentQuestionIndex + 1} of {questions.length}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAiPrompt(!showAiPrompt)}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            AI Assistant
          </Button>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-medium text-white">
            {currentQuestion.text}
          </h3>
          
          {showAiPrompt && (
            <div className="bg-[#F8B500]/10 p-4 rounded-lg">
              <p className="text-[#F8B500]">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                {currentQuestion.aiPrompt}
              </p>
            </div>
          )}

          {currentQuestion.type === 'multiselect' ? (
            <div className="space-y-2">
              {currentQuestion.options?.map(option => (
                <button
                  key={option}
                  onClick={() => toggleOption(option)}
                  className={`w-full text-left p-3 rounded-lg border ${
                    selectedOptions.includes(option)
                      ? 'bg-[#F8B500]/10 border-[#F8B500] text-white'
                      : 'border-gray-700 text-gray-300 hover:border-[#F8B500]'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : (
            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              className={`w-full p-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#F8B500] focus:border-transparent ${
                currentQuestion.type === 'longtext' || currentQuestion.type === 'link' ? 'h-48' : 'h-32'
              }`}
              placeholder={currentQuestion.type === 'link' ? 'Paste your link here...' : 'Type your answer here...'}
            />
          )}
        </div>

        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
              const prevAnswer = answers[questions[currentQuestionIndex - 1].id];
              if (Array.isArray(prevAnswer)) {
                setSelectedOptions(prevAnswer);
              } else {
                setCurrentAnswer(prevAnswer || '');
              }
            }}
            disabled={currentQuestionIndex === 0}
          >
            Back
          </Button>
          
          <Button 
            onClick={handleNext}
            disabled={
              currentQuestion.type === 'multiselect'
                ? selectedOptions.length === 0
                : !currentAnswer.trim()
            }
          >
            {currentQuestionIndex === questions.length - 1 ? 'Submit' : 'Next'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-medium text-white mb-4">Progress Overview</h3>
        <div className="space-y-2">
          {questions.map((q, index) => (
            <div
              key={q.id}
              className="flex items-center space-x-2"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                answers[q.id] ? 'bg-[#F8B500] text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {index + 1}
              </div>
              <span className={`text-sm ${
                answers[q.id] ? 'text-white' : 'text-gray-400'
              }`}>
                {q.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {showScoreModal && submissionScore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#212323] rounded-lg border border-gray-700 p-8 max-w-2xl w-full mx-4 space-y-6">
            <h2 className="text-2xl font-bold text-white">
              Submission Analysis
            </h2>
            
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-block rounded-full bg-[#F8B500]/10 p-6 mb-4">
                  <span className="text-4xl font-bold text-[#F8B500]">
                    {Math.round(submissionScore.totalScore)}%
                  </span>
                </div>
                <p className="text-gray-300">
                  Overall Alignment Score
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white">Detailed Breakdown</h3>
                {submissionScore.breakdown.map(({ category, score, feedback, weight }) => (
                  <div key={category} className="bg-gray-800 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium capitalize">{category}</span>
                      <span className={`text-sm font-medium ${
                        score >= 90 ? 'text-green-500' :
                        score >= 70 ? 'text-[#F8B500]' :
                        'text-red-500'
                      }`}>
                        {Math.round(score)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full ${
                          score >= 90 ? 'bg-green-500' :
                          score >= 70 ? 'bg-[#F8B500]' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-300">{feedback}</p>
                  </div>
                ))}
              </div>

              {submissionScore.recommendations.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white">Recommendations</h3>
                  <ul className="space-y-2">
                    {submissionScore.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-300">
                        <span className="text-[#F8B500]">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowScoreModal(false)}
                >
                  Revise Submission
                </Button>
                <Button
                  onClick={() => {
                    console.log('Final submission with score:', {
                      answers,
                      score: submissionScore
                    });
                  }}
                >
                  Submit Anyway
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}